import React, { useState, useEffect, useCallback } from "react";
import Button from "components/button";
import ConfirmationModal from "components/shared/confirmation-modal";
import FleetInformationAccordion from "./components/fleet-information-accordion";
import FleetDocumentAccordion from "./components/fleet-document-accordion";
import FleetComplianceAccordion from "./components/fleet-compliance-accordion";
import useAddFleet from "./useAddFleet";
import { Loader } from "components/spinner/loader";

export const addFleetPageRouteName = "/master-data/fleet/add";

const AddFleetPage = () => {
  const {
    t,
    openSections,
    toggleSection,
    form,
    handleNextFromInformation,
    handleNextFromDocuments,
    handleSaveDraft,
    handleSave,
    isSubmitting,
    documentFields,
    handleAddDocument,
    handleRemoveDocument,
    vehicleTypeOptions,
    vehicleModelOptions,
    freezerModelOptions,
    vendorOptions,
    driverOptions,
    onSearchDrivers,
    onSearchVendors,
    isUploadingPicture,
    handleUploadFleetPicture,
    isUploadingDoc,
    handleUploadDocument,
    expiryWarnings,
    isDirty,
    showCancelModal,
    setShowCancelModal,
    navigate,
    isLoading,
    handleDiscard,
  } = useAddFleet();

  const [showUnsavedModal, setShowUnsavedModal] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);

  useEffect(() => {
    const handleGlobalBack = (e: Event) => {
      if (isDirty) {
        e.preventDefault();
        setShowUnsavedModal(true);
      }
    };

    window.addEventListener("global-back-button-click", handleGlobalBack);

    return () =>
      window.removeEventListener("global-back-button-click", handleGlobalBack);
  }, [isDirty]);

  const handleLocalDiscard = useCallback(() => {
    setShowUnsavedModal(false);
    handleDiscard();
  }, [handleDiscard]);

  const handleConfirmSaveDraft = useCallback(() => {
    setShowUnsavedModal(false);
    handleSaveDraft();
  }, [handleSaveDraft]);

  const handleConfirmSave = useCallback(async () => {
    setShowSaveModal(false);
    await handleSave();
  }, [handleSave]);

  const handleSaveClick = useCallback(async () => {
    const isValid = await form.trigger();

    if (!isValid) {
      return;
    }

    setShowSaveModal(true);
  }, [form]);

  if (isLoading) {
    return (
      <div className="flex min-h-[calc(100vh-200px)] items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-112px)] -mb-3 -mx-3">
      <div className="flex-1 flex flex-col gap-4 p-3 pb-20">
        <FleetInformationAccordion
          vehicleTypeOptions={vehicleTypeOptions}
          vehicleModelOptions={vehicleModelOptions}
          freezerModelOptions={freezerModelOptions}
          vendorOptions={vendorOptions}
          driverOptions={driverOptions}
          onSearchDrivers={onSearchDrivers}
          onSearchVendors={onSearchVendors}
          isOpen={openSections.fleetInformation}
          onToggle={() => toggleSection("fleetInformation")}
          onNext={handleNextFromInformation}
          form={form}
          t={t}
          isUploadingPicture={isUploadingPicture}
          onUploadPicture={handleUploadFleetPicture}
        />

        <FleetDocumentAccordion
          isOpen={openSections.fleetDocuments}
          onToggle={() => toggleSection("fleetDocuments")}
          onNext={handleNextFromDocuments}
          form={form}
          t={t}
          fields={documentFields}
          onAdd={handleAddDocument}
          onRemove={handleRemoveDocument}
          isUploadingDoc={isUploadingDoc}
          onUpload={handleUploadDocument}
          expiryWarnings={expiryWarnings}
        />

        <FleetComplianceAccordion
          isOpen={openSections.fleetCompliance}
          onToggle={() => toggleSection("fleetCompliance")}
          form={form}
          t={t}
        />
      </div>

      <div className="sticky bottom-0 bg-white border-t border-border px-6 py-3 flex justify-end gap-2 z-20 mt-auto">
        <Button
          type="button"
          variant="outline"
          color="tertiary"
          data-testid="btn-save-draft-fleet"
          onClick={handleSaveDraft}
          disabled={isSubmitting}
        >
          {t("btn-save-draft")}
        </Button>

        <Button
          type="button"
          variant="fill"
          color="primary"
          data-testid="btn-save-fleet"
          onClick={handleSaveClick}
          disabled={isSubmitting}
        >
          {t("btn-save")}
        </Button>
      </div>

      <ConfirmationModal
        open={showUnsavedModal}
        title={t("modal-unsaved-title" as any) || "You have unsaved changes"}
        message={
          t("modal-unsaved-message" as any) ||
          "This action cannot be undone. Save as draft or discard before leaving."
        }
        cancelLabel={t("btn-discard-changes" as any) || "Discard Changes"}
        confirmLabel={t("btn-save-as-draft" as any) || "Save as Draft"}
        onCancel={handleLocalDiscard}
        onConfirm={handleConfirmSaveDraft}
        icon="info"
      />

      <ConfirmationModal
        open={showSaveModal}
        title={t("modal-save-title" as any) || "Save this data?"}
        message={
          t("modal-save-message" as any) ||
          "Are you sure you want to save this information?"
        }
        cancelLabel={t("btn-cancel" as any) || "Cancel"}
        confirmLabel={t("btn-save" as any) || "Save"}
        onCancel={() => setShowSaveModal(false)}
        onConfirm={handleConfirmSave}
        icon="info"
        loading={isSubmitting}
      />

      <ConfirmationModal
        open={showCancelModal}
        title={t("modal-save-draft-title") || "Save as Draft?"}
        message={
          t("modal-save-draft-message") ||
          "You have unsaved changes. Do you want to save this fleet as a draft?"
        }
        confirmLabel={t("btn-save-draft") || "Save as Draft"}
        cancelLabel={t("modal-confirm-stay") || "Keep Editing"}
        onConfirm={() => {
          setShowCancelModal(false);
          handleSaveDraft();
        }}
        onCancel={() => setShowCancelModal(false)}
      >
        <div className="mt-2 mb-2 w-full px-2">
          <button
            type="button"
            className="w-full text-center text-sm font-medium text-error hover:underline"
            onClick={handleDiscard}
          >
            {t("modal-confirm-discard") || "Discard and leave"}
          </button>
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default AddFleetPage;
